import { pickBy } from '../../utils/pickBy/pickBy.js';
import { http } from './services/http.js';

const WIDGET_VERSION = '1.1.2'; // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
const WIDGET_NAME = 'oc-connect-widget';
const ODDS_COMPARISON_WIDGET_NAME = 'oc-odds-comparison-widget';
const OFFERS_WIDGET_NAME = 'oc-offers-widget';
const BET_BUILDER_WIDGET_NAME = 'oc-bet-builder-widget';
const VALUE_BET_WIDGET_NAME = 'oc-value-bet-widget';
const ODDS_GRID_WIDGET_NAME = 'oc-odds-grid-widget';
const ODDS_GRID_VERTICAL_WIDGET_NAME = 'oc-odds-grid-vertical-widget';
const GAME_MATCHUP_WIDGET_NAME = 'oc-game-matchup-widget';

const mapTypeToWidget = {
  'comparison-responsive-table': {
    name: ODDS_COMPARISON_WIDGET_NAME,
    importScript: () => import(`../${ODDS_COMPARISON_WIDGET_NAME}/index.js`)
  },
  offers: {
    name: OFFERS_WIDGET_NAME,
    importScript: () => import(`../${OFFERS_WIDGET_NAME}/index.js`)
  },
  'bet-builder': {
    name: BET_BUILDER_WIDGET_NAME,
    importScript: () => import(`../${BET_BUILDER_WIDGET_NAME}/index.js`)
  },
  'value-bet': {
    name: VALUE_BET_WIDGET_NAME,
    importScript: () => import(`../${VALUE_BET_WIDGET_NAME}/index.js`)
  },
  'odds-grid': {
    name: ODDS_GRID_WIDGET_NAME,
    importScript: () => import(`../${ODDS_GRID_WIDGET_NAME}/index.js`)
  },
  'odds-grid-vertical': {
    name: ODDS_GRID_VERTICAL_WIDGET_NAME,
    importScript: () => import(`../${ODDS_GRID_VERTICAL_WIDGET_NAME}/index.js`)
  },
  'game-matchup': {
    name: GAME_MATCHUP_WIDGET_NAME,
    importScript: () => import(`../${GAME_MATCHUP_WIDGET_NAME}/index.js`)
  }
};

const template = document.createElement('template');

template.innerHTML = /* html */ `
  <style>
    :host {
      display: contents;
    }

    :host([hidden]),
    [hidden] {
      display: none !important;
    }
  </style>

  <slot name="loading" hidden></slot>
  <slot name="error" hidden></slot>
  <slot id="slots-placeholder" hidden></slot>
`;

class OCConnectWidget extends HTMLElement {
  constructor() {
    super();

    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }

    this._version = WIDGET_VERSION;
  }

  async connectedCallback() {
    const data = await this.#fetchData();

    if (!data) {
      return;
    }

    const widgetMeta = mapTypeToWidget[data?.journey];

    if (!widgetMeta) {
      return;
    }

    // dynamically import the widget
    await widgetMeta.importScript();

    const widgetEl = document.createElement(widgetMeta.name);

    for (const attr of this.attributes) {
      widgetEl.setAttribute(attr.name, attr.value);
    }

    widgetEl._initialData = data;
    widgetEl.part = widgetMeta.name;

    this.shadowRoot
      .getElementById('slots-placeholder')
      ?.assignedNodes({ flatten: true })
      .forEach(el => {
        // Pass any slot elements to child component.
        if (el instanceof HTMLSlotElement && el.name) {
          const distributedNode = document.createElement('div');
          distributedNode.setAttribute('slot', el.name);
          distributedNode.innerHTML = el.innerHTML;
          widgetEl.appendChild(distributedNode);
        }
      });

    this.shadowRoot.appendChild(widgetEl);

    this.#applyGazzettaLayoutFix();
  }

  /**
   * NOTE: Ugly hack specific to Gazzetta's integration of the widget.
   * It can be safely removed in the future only if Gazzetta decides to fix it on their side.
   *
   * This is HTML tree that Gazzetta sometimes renderes the widget:
   * <div class="adv">
   *   <div>
   *     <div class="ocw"><!-- Widget's code goes here --></div>
   *   </div>
   * </div>
   *
   * The element with `adv` class name is a flexbox container, but the immediate `<div>` child
   * has no constraints, eg width or min-width, therefore the widget's table (which does not wrap by default)
   * exceeds the desired width of the container.
   *
   * The solution provided is specific for Gazzetta and we just apply a constraint
   * to the affected container so that the widget does not overflow.
   */
  #applyGazzettaLayoutFix() {
    const isGazzettaWebsite = window.location.host.includes('gazzetta.it');
    const gazzettaAdvContainer = this.closest('.adv > div');

    if (isGazzettaWebsite && gazzettaAdvContainer) {
      gazzettaAdvContainer.style.minWidth = 0;
    }
  }

  async #fetchData() {
    const loadingSlot = this.shadowRoot.querySelector('slot[name="loading"]');
    const errorSlot = this.shadowRoot.querySelector('slot[name="error"]');

    loadingSlot.hidden = false;

    try {
      const slotAreaAttr = this.getAttribute('slot-id');
      const overrideUrlAttr = this.getAttribute('override-url');
      const betIds = this.getAttribute('bet-ids');
      const ocBetIds = betIds?.split(',')?.map(id => parseInt(id));

      const contextualConfig = pickBy(
        {
          sport: this.getAttribute('sport'),
          participants: this.getAttribute('participants'),
          participant: this.getAttribute('participant'),
          competition: this.getAttribute('competition'),
          ocSubeventId: this.getAttribute('subevent-id'),
          ocBetIds
        },
        key => key
      );

      const data = await http.fetchCampaignData(
        slotAreaAttr,
        overrideUrlAttr,
        Object.keys(contextualConfig).length !== 0 ? contextualConfig : void 0
      );

      this.dispatchEvent(
        new CustomEvent(`${WIDGET_NAME}:load-success`, {
          bubbles: true,
          composed: true,
          detail: { data }
        })
      );

      return data;
    } catch (error) {
      errorSlot.hidden = false;

      this.dispatchEvent(
        new CustomEvent(`${WIDGET_NAME}:load-error`, {
          bubbles: true,
          composed: true,
          detail: { error }
        })
      );
    } finally {
      loadingSlot.hidden = true;

      this.dispatchEvent(
        new Event(`${WIDGET_NAME}:load`, {
          bubbles: true,
          composed: true
        })
      );
    }
  }
}

if (window.customElements && !window.customElements.get(WIDGET_NAME)) {
  window.customElements.define(WIDGET_NAME, OCConnectWidget);
}
